import SEO from "@/components/SEO";
import { PageHero, Reveal, Kicker, UL, Callout, Rich, BtnPrimary, BtnGhost, Marquee } from "@/components/Bits";

const CHAPTERS = [
  {
    n: "01",
    title: "Post your work via Send Work",
    body: "Open the Middle Man app, choose a category and sub-category, describe the task, and share your location. Every job created through Send Work builds a permanent in-app record of the request, price, time and terms.",
  },
  {
    n: "02",
    title: "Match with a nearby verified helper",
    body: "GPS-first search shows the closest available Middle Man. Every Middle Man is verified with Aadhaar / PAN checks and address validation before activation — nearby helpers are always prioritised.",
  },
  {
    n: "03",
    title: "OTP-secured start & finish",
    body: "Work starts and finishes only after you, the Customer, share an OTP. No OTP, no job — this protects both sides and creates proof of exactly when work began and ended.",
  },
  {
    n: "04",
    title: "Track live, in real time",
    body: "See your Middle Man on the map with a real-time ETA while the job is underway, from acceptance to arrival to completion.",
  },
  {
    n: "05",
    title: "Rate, review, resolve",
    body: "Rate the work when it's done — ratings keep quality high and suspicious or fraudulent accounts are suspended. If anything goes wrong, a one-tap complaint route with photo evidence is built into every job.",
  },
];

const SEND_WORK_BENEFITS = [
  "A permanent in-app record of the request, price, time and terms.",
  "OTP-secured start & completion for eligible categories.",
  "Live tracking during the job.",
  "A one-tap complaint / dispute route with photo evidence.",
  "A rated review at the end that helps other customers.",
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page">
      <SEO
        title="How It Works"
        description="How Middle Man works — post a job via Send Work, get matched with a nearby verified Middle Man, OTP-secured start & finish, live tracking, and rated reviews."
        path="/how-it-works"
      />
      <PageHero
        kicker="How it works"
        title="From request to done — in five chapters."
        subtitle="A simple, transparent flow designed for trust: verified helpers, OTP-secured jobs, and live tracking on every booking."
      />
      <Marquee items={["Post work", "Match nearby", "OTP start", "Live tracking", "OTP finish", "Rate & review"]} />

      <div className="mx-auto max-w-5xl px-6 py-20 md:py-28">
        <div className="space-y-16" data-testid="chapters">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.04}>
              <div className="grid gap-6 border-t border-ink/15 pt-10 md:grid-cols-[140px_1fr]">
                <span className="font-serif text-6xl font-semibold leading-none text-brand md:text-7xl">{c.n}</span>
                <div>
                  <h2 className="font-serif text-2xl font-semibold tracking-tight md:text-3xl">{c.title}</h2>
                  <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-mute md:text-base">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20">
          <div className="border border-line bg-white p-8 md:p-12" data-testid="send-work-card">
            <Kicker>Always use Send Work</Kicker>
            <h2 className="mt-5 font-serif text-2xl font-semibold tracking-tight md:text-3xl">
              One flow. Full protection.
            </h2>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-mute">
              Customers are STRONGLY encouraged to create every job via the in-app <strong className="font-semibold text-ink">Send Work</strong> flow (not verbal / WhatsApp-only bookings). This gives you:
            </p>
            <div className="mt-6 max-w-2xl"><UL items={SEND_WORK_BENEFITS} /></div>
            <div className="mt-8 max-w-2xl">
              <Callout>
                <Rich text="Off-platform jobs (bypassing Send Work) waive dispute-resolution support and any refunds under the Cancellation & Refund Policy." />
              </Callout>
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-16">
          <div className="flex flex-wrap gap-4">
            <BtnPrimary to="/services" testid="hiw-explore-services-btn">Explore services</BtnPrimary>
            <BtnGhost to="/download" testid="hiw-download-btn">Get the app</BtnGhost>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
